/**
 * Routes every quick-view fetch to the dedicated quick-view template.
 *
 * Horizon's quick-add component fetches a product page and morphs in the element
 * marked [data-product-grid-content], which its own product-information section
 * renders. This store's product page uses a custom section, so that element is
 * served from templates/product.quick-view.json instead.
 *
 * Patching fetchProductPage (rather than the card's link) means the change
 * applies everywhere quick view is used — collection, search, home and product
 * pages alike — without altering any card's navigation URL.
 */
customElements.whenDefined('quick-add-component').then(() => {
  const QuickAdd = customElements.get('quick-add-component');
  if (!QuickAdd || QuickAdd.prototype.__miQuickViewPatched) return;

  const original = QuickAdd.prototype.fetchProductPage;
  if (typeof original !== 'function') return;

  QuickAdd.prototype.fetchProductPage = function (productPageUrl) {
    let target = productPageUrl;

    try {
      const url = new URL(String(productPageUrl), window.location.origin);
      url.searchParams.set('view', 'quick-view');
      target = url.toString();
    } catch {
      // leave the URL untouched if it can't be parsed
    }

    return original.call(this, target);
  };

  QuickAdd.prototype.__miQuickViewPatched = true;
});

/**
 * Keeps variant switches *inside* the already-open Quick View modal on the
 * quick-view template too.
 *
 * The modal's initial content is fetched with ?view=quick-view (patched above),
 * but every subsequent variant click re-fetches through variant-picker.js's own
 * buildRequestUrl(), which only preserves `view` by reading it off the *page's*
 * URL (window.location.search) - not the modal's. On any page other than the
 * quick-view product page itself that param is absent, so the re-fetch silently
 * falls back to the default product template. That markup's block ids don't
 * match the modal's, so product-price.js/media-gallery.js's update listeners
 * find no matching block and never touch the DOM - the variant id/radio state
 * updates, but the displayed price and image stay stuck on whatever was
 * selected when the modal opened.
 */
customElements.whenDefined('variant-picker').then(() => {
  const VariantPicker = customElements.get('variant-picker');
  if (!VariantPicker || VariantPicker.prototype.__miQuickViewPatched) return;

  const original = VariantPicker.prototype.buildRequestUrl;
  if (typeof original !== 'function') return;

  VariantPicker.prototype.buildRequestUrl = function (...args) {
    const url = original.apply(this, args);

    if (!this.closest('quick-add-dialog')) return url;

    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.searchParams.has('view')) {
        parsed.searchParams.set('view', 'quick-view');
        return parsed.toString();
      }
    } catch {
      // leave the URL untouched if it can't be parsed
    }

    return url;
  };

  VariantPicker.prototype.__miQuickViewPatched = true;
});
